/**
 * KnowledgeBasePage — PAGE-008, Admin only.
 * Orchestrates: search panel, upload button, document list.
 * Admin gate is enforced by AdminRoute in App.tsx (role guard + redirect).
 * This component adds a secondary runtime check for belt-and-suspenders safety.
 *
 * FR-073, FR-080, FR-081, FR-082
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { KBDocumentList } from './KBDocumentList';
import { KBUploadDialog } from './KBUploadDialog';
import { KBDeleteConfirm } from './KBDeleteConfirm';
import { KBSearchPanel } from './KBSearchPanel';
import type { KBDocumentResponse } from '@/hooks/useKBDocuments';

export function KnowledgeBasePage() {
  const { isAdmin, isLoading } = useAuth();
  const navigate = useNavigate();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KBDocumentResponse | null>(null);

  // Secondary runtime admin guard (primary is AdminRoute in App.tsx)
  if (!isLoading && !isAdmin) {
    toast.error('You do not have permission to manage the knowledge base.');
    navigate('/dashboard', { replace: true });
    return null;
  }

  const handleDeleteRequest = (doc: KBDocumentResponse) => {
    if (doc.client_id === null) {
      // Show the dialog — it will explain global docs cannot be deleted
      setDeleteTarget(doc);
    } else {
      setDeleteTarget(doc);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Knowledge Base</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Upload and manage legislation documents used by the Compliance Agent for RAG-grounded analysis.
          </p>
        </div>
        <Button onClick={() => { setUploadOpen(true); }} aria-label="Upload a new legislation document">
          <Upload className="h-4 w-4" aria-hidden="true" />
          Upload Document
        </Button>
      </div>

      {/* Semantic search panel */}
      <KBSearchPanel />

      <Separator />

      {/* Document list */}
      <KBDocumentList
        onDelete={handleDeleteRequest}
        onUpload={() => { setUploadOpen(true); }}
      />

      {/* Upload dialog */}
      <KBUploadDialog open={uploadOpen} onClose={() => { setUploadOpen(false); }} />

      {/* Delete confirmation */}
      <KBDeleteConfirm
        docId={deleteTarget?.id ?? null}
        docTitle={deleteTarget?.title ?? ''}
        isGlobal={deleteTarget?.client_id === null}
        onClose={() => { setDeleteTarget(null); }}
      />
    </div>
  );
}
